import type { ImageAsset, LinkField } from "@/types/references";

/**
 * The `claim_offer_page` entry as `/retrieve` returns it. Every field is
 * optional because authors publish incrementally.
 */
export interface HeroSection {
	blockType: "hero";
	headline?: string;
	copy?: string;
	copy_no_consultant?: string;
	hero_image_desktop_left?: ImageAsset;
	hero_image_desktop_right?: ImageAsset;
	hero_image_mobile?: ImageAsset;
}

export interface BenefitItem {
	label?: string;
}

export interface FormSection {
	blockType: "form";
	/** Heading above the card, not the tag inside it. */
	eyebrow?: string;
	desktop_image?: ImageAsset;
	mobile_image?: ImageAsset;
	badge?: string;
	headline?: string;
	copy?: string;
	copy_no_consultant?: string;
	email_helper?: string;
	cta_label?: string;
	consent_copy?: string;
	privacy_policy_link?: LinkField;
	benefits_heading?: string;
	benefits?: BenefitItem[];
}

export type CardTagColor = "Red" | "Green";
export type CardTagIcon = "chef-hat" | "star";

export interface CardCta {
	link?: LinkField;
	open_in_new_tab?: boolean;
	assistive_text?: string;
}

export interface OfferCard {
	image?: ImageAsset;
	tag_color?: CardTagColor;
	tag_icon?: CardTagIcon;
	tag_label?: string;
	headline?: string;
	copy?: string;
	cta?: CardCta;
}

export interface CardsSection {
	blockType: "cards";
	eyebrow?: string;
	card?: OfferCard[];
}

export interface ValueItem {
	image?: ImageAsset;
	title?: string;
	caption?: string;
}

export interface ValuesSection {
	blockType: "values";
	headline?: string;
	copy?: string;
	items?: ValueItem[];
}

export type ClaimOfferSection =
	HeroSection | FormSection | CardsSection | ValuesSection;

export type ClaimOfferSectionType = ClaimOfferSection["blockType"];

export type SectionOf<T extends ClaimOfferSectionType> = Extract<
	ClaimOfferSection,
	{ blockType: T }
>;

export interface OfferTerms {
	modal_title?: string;
	body?: string;
	acknowledge_label?: string;
}

export interface Offer {
	discount_label?: string;
	minimum_spend?: string;
	/** ISO date; authors pick midnight Central in the CMS. */
	expiration_date?: string;
	terms?: OfferTerms;
}

export interface ClaimOutcomeContent {
	icon_image?: ImageAsset;
	headline?: string;
	copy?: string;
	followup_heading?: string;
	followup_copy?: string;
	followup_copy_no_consultant?: string;
}

export interface ClaimPanels {
	desktop_image?: ImageAsset;
	mobile_image?: ImageAsset;
	success?: ClaimOutcomeContent;
	ineligible?: ClaimOutcomeContent;
}

export interface ClaimOfferPageData {
	title: string;
	seasonal_theme?: { seasonal_theme?: string | null };
	offer?: Offer;
	sections?: ClaimOfferSection[];
	claim?: ClaimPanels;
}

export function pickSection<T extends ClaimOfferSectionType>(
	sections: ClaimOfferSection[] | undefined,
	type: T
): SectionOf<T> | undefined {
	return sections?.find((section) => section.blockType === type) as
		SectionOf<T> | undefined;
}
